import {
  useGetRecoveryConfigQuery,
  useGetRecoveryStatusQuery,
} from 'api/recoveryAi'
import Spinner from 'components/Spinner'
import InnerTabs, { TabT } from 'components/Tabs/InnerTabs'
import useHashTab from 'components/Tabs/useHashTab'
import useFlag from 'hooks/useFlag'
import { buildTranslate } from 'locales'
import React, { useCallback, useMemo } from 'react'
import { history } from 'store'
import GamingReview from '../GamingReview'
import RestrictedCustomers from '../RestrictedCustomers'
import ActiveLocationsPane from './ActiveLocationsPane'
import AgentIntroductionPane from './AgentIntroductionPane'
import ApprovedRewardsPane from './ApprovedRewardsPane'
import BrandVoicePane from './BrandVoicePane'
import CannedResponsesPane from './CannedResponsesPane'
import DailyBudgetPane from './DailyBudgetPane'
import EscalationContactsPane from './EscalationContacts'
import EscalationRulesPane from './EscalationRulesPane'
import RecoveryGenerosityPane from './RecoveryGenerosityPane'
import SetupGuidePane from './SetupGuidePane'
import type { SettingsTabKey } from './tabKeys'

// The canonical key list + its type live in the dependency-free ./tabKeys leaf
// module; re-exported here so existing importers (the hub test) keep importing
// from '.'. The decision-free panes (Recovery
// Generosity / Brand Voice / Escalation Rules) are replaced by AI-29 / AI-28 /
// AI-33 — each edits ONLY its own `component:` line below. Approved Rewards
// (AI-30), Active Locations (AI-31), Escalation Contacts (AI-32) and Restricted
// Customers (AI-41, appended last per the Figma order) are all live now — no
// pane remains gated.
export { SETTINGS_TAB_KEYS } from './tabKeys'
export type { SettingsTabKey } from './tabKeys'

const t = buildTranslate('recovery_ai.settings')

const Settings = () => {
  // recovery-ai-setup-guide gates BOTH dev-only tabs — "Canned responses" and
  // "Setup guide" (fail-soft absent by default). Both tabs are dev-only, so
  // sharing one purely-cosmetic gate is intentional — don't split them apart.
  const devOnlyTabsEnabled = useFlag('recovery-ai-setup-guide', false)
  // recovery-ai-gaming-review gates the whole "Suspected abuse" feature — this
  // tab AND the escalation-rules "Suspected abuse" section (EscalationRulesPane /
  // EscalationRulesModalBody). Off (default) → both hidden; on → the tab shows the
  // real queue (no placeholder) and the section renders.
  const gamingReviewEnabled = useFlag('recovery-ai-gaming-review', false)
  // AI-576: the daily-budget pane is dark-launched — present only when
  // recovery-ai-daily-budget is on (fail-soft absent by default).
  const dailyBudgetEnabled = useFlag('recovery-ai-daily-budget', false)
  // AI-1110: the same entitlement probe RecoveryAI/index.tsx already reads
  // (shared cache entry, not a second request) — threaded down as
  // `isActivated` so Recovery generosity / Brand voice / Approved rewards can
  // withhold Save-and-test once the merchant is live.
  //   context: `isLoading` gates the loading case separately from the error
  //   case on purpose — they need OPPOSITE defaults. While the probe is still
  //   in flight, `isActivated` defaults to true (withhold Save-and-test) so an
  //   activated merchant never gets a clickable Save-and-test on a cold load
  //   that then vanishes the instant status resolves. Once the request
  //   SETTLES — success or failure — `Boolean(recoveryStatus?.active)` takes
  //   over: a FAILED fetch leaves `recoveryStatus` undefined, so this falls
  //   through to `false` (fail-soft — never wrongly HIDE the button on a
  //   failed fetch), while a resolved fetch uses the real value.
  //   Traded-off flash, accepted: a pre-activation merchant on a cold load
  //   still sees Save-only styling gain a Save-and-test button (+ a kind
  //   flip) once the probe resolves — the deliberate mirror of the bug this
  //   fixed. Chosen over threading a third loading state through the panes'
  //   footers because the cache is usually already warm from
  //   RecoveryAI/index.tsx's own probe, so this rarely fires in practice.
  const { data: recoveryStatus, isLoading: isRecoveryStatusLoading } =
    useGetRecoveryStatusQuery()
  // Older backends omit agent_name and would drop the PUT, so hide the tab.
  const { data: recoveryConfig, isLoading: isRecoveryConfigLoading } =
    useGetRecoveryConfigQuery()
  const agentNameSupported =
    recoveryConfig !== undefined && 'agent_name' in recoveryConfig
  const isActivated = isRecoveryStatusLoading
    ? true
    : Boolean(recoveryStatus?.active)
  // Titles must resolve at render time (after react-redux-i18n loads
  // localizations), so the array can't hoist to module scope; memoized on the
  // gating flags. The `key:` annotation keeps the keys compile-checked against
  // SETTINGS_TAB_KEYS.
  const tabs: (TabT & { key: SettingsTabKey })[] = useMemo(
    () => [
      {
        key: 'recovery_generosity',
        title: t('tabs.recovery_generosity'),
        // Explicit Save/Save-and-test pane (AI-62 staged save): unmount when its
        // tab is inactive so an unsaved stance edit is discarded on tab exit and
        // (re)entry always shows the saved config (matches brand_voice /
        // escalation_rules).
        unmountInactive: true,
        component: <RecoveryGenerosityPane isActivated={isActivated} />, // AI-29
      },
      {
        key: 'brand_voice',
        title: t('tabs.brand_voice'),
        // Explicit Save/Save-and-test pane: unmount when its tab is inactive so
        // unsaved edits are discarded on exit and (re)entry always shows the
        // saved config (strict — matches the parent-tab remount behavior).
        unmountInactive: true,
        component: <BrandVoicePane isActivated={isActivated} />, // AI-28
      },
      ...(agentNameSupported
        ? [
            {
              key: 'agent_introduction' as const,
              title: t('tabs.agent_introduction'),
              unmountInactive: true,
              component: <AgentIntroductionPane />,
            },
          ]
        : []),
      {
        key: 'active_locations',
        title: t('tabs.active_locations'),
        // Explicit Save pane (AI-31 staged save): unmount when its tab is
        // inactive so an unsaved location-selection edit is discarded on tab
        // exit and (re)entry always shows the saved config (matches the other
        // staged-save panes).
        unmountInactive: true,
        component: <ActiveLocationsPane />, // AI-31
      },
      {
        key: 'approved_rewards',
        title: t('tabs.approved_rewards'),
        // Explicit Save/Save-and-test pane (AI-30 staged save): unmount when its
        // tab is inactive so an unsaved reward-selection edit is discarded on tab
        // exit and (re)entry always shows the saved config (matches brand_voice /
        // escalation_rules).
        unmountInactive: true,
        component: <ApprovedRewardsPane isActivated={isActivated} />, // AI-30
      },
      {
        key: 'escalation_contacts',
        title: t('tabs.escalation_contacts'),
        // AI-32: now live (the recipient-user contacts backend, BE-5, shipped).
        // Explicit-save pane — unmount when inactive so an unsaved recipient
        // selection is discarded on tab exit and (re)entry shows the saved set
        // (matches the sibling save-bearing panes).
        unmountInactive: true,
        component: <EscalationContactsPane />,
      },
      {
        key: 'escalation_rules',
        title: t('tabs.escalation_rules'),
        // Explicit Save/Save-and-test pane: unmount when inactive so unsaved
        // threshold edits are discarded on tab exit (strict — see brand_voice).
        unmountInactive: true,
        component: <EscalationRulesPane />, // AI-33
      },
      // AI-576: global daily-budget writer. Flag-gated — spread in only when
      // recovery-ai-daily-budget is on, so the tab (and its #daily_budget
      // deep-link) are absent by default. unmountInactive defers its config query
      // to tab-open and discards unsaved edits on exit (matches the sibling
      // save-bearing panes).
      ...(dailyBudgetEnabled
        ? [
            {
              key: 'daily_budget' as const,
              title: t('tabs.daily_budget'),
              unmountInactive: true,
              component: <DailyBudgetPane />,
            },
          ]
        : []),
      {
        key: 'restricted_customers',
        title: t('tabs.restricted_customers'),
        // AI-41: a live list + confirm-modal pane with no unsaved-edit state, so
        // (unlike the explicit-save panes) it does NOT need unmountInactive — a
        // remount just refetches via its providesTags cache.
        component: <RestrictedCustomers />,
      },
      // recovery-ai-gaming-review gates the whole "Suspected abuse" feature: the
      // tab (and its #gaming_review deep-link) are absent unless the flag is on,
      // mirroring the escalation-rules "Suspected abuse" section gated on the same
      // flag. When present it renders the real queue (no placeholder);
      // unmountInactive defers useGetGamingReviewCustomersQuery to tab-open.
      ...(gamingReviewEnabled
        ? [
            {
              key: 'gaming_review' as const,
              title: t('tabs.gaming_review'),
              unmountInactive: true,
              component: <GamingReview />,
            },
          ]
        : []),
      // Dev-only tabs, grouped at the very bottom, gated together on the shared
      // recovery-ai-setup-guide flag (see devOnlyTabsEnabled). `dividerAbove` on
      // the first of the group draws a hairline demarcating these dev-mode tabs
      // from the always-on config above; because the whole group is spread in
      // only when the flag is on, that divider appears/disappears with the tabs.
      //   context:
      //     setup_guide re-opens the onboarding wizard (non-destructive,
      //     navigation-only) — no unmountInactive, no unsaved local edits.
      //     canned_responses (AI-249) is the per-merchant read-only view, last;
      //     unmountInactive defers its query until tab-open.
      ...(devOnlyTabsEnabled
        ? [
            {
              key: 'setup_guide' as const,
              title: t('tabs.setup_guide'),
              dividerAbove: true,
              component: <SetupGuidePane />,
            },
            {
              key: 'canned_responses' as const,
              title: t('tabs.canned_responses'),
              unmountInactive: true,
              component: <CannedResponsesPane />,
            },
          ]
        : []),
    ],
    [
      devOnlyTabsEnabled,
      dailyBudgetEnabled,
      gamingReviewEnabled,
      agentNameSupported,
      isActivated,
    ]
  )

  // InnerTabs reads the hash only on mount; drive selection from it here.
  const tabKeys = useMemo(() => tabs.map(tab => tab.key), [tabs])
  const [activeTab] = useHashTab(tabKeys, tabKeys[0])
  const handleTabChange = useCallback((key: string) => {
    history.replace({ ...history.location, hash: key })
  }, [])

  // Wait for the config so a deep link to Agent introduction can resolve.
  if (isRecoveryConfigLoading) {
    return (
      <Spinner isLoading size="4x" className="text-center margin-top-huge" />
    )
  }

  return (
    <InnerTabs
      tabs={tabs}
      ariaLabel={t('tablist_aria_label')}
      activeKey={activeTab}
      onChange={handleTabChange}
    />
  )
}

export default Settings
